import { allAmountsSame, getHighestAmountIndex } from '@afterpay/utils'
import { CheckoutPaymentSchedule, Cohort, Country, FirstPaymentType } from '@afterpaytouch/portal-api'
import { useTranslation } from 'next-i18next'
import {
  useAppRouter,
  useCalculatedPaymentSchedules,
  useCheckoutState,
  useCohort,
  useCountry,
  useExperiment,
  useGetPreferredPaymentDayQuery,
  useIsBillingAgreementApprovalProcess,
  useIsConsumerLendingOrder,
  useRollout,
} from '../'
import { ExperimentValue, Features, money } from '../../model'
import { useRouter } from 'next/router'
import { Route } from '../../config/router'
import { useFlag } from '@afterpaytouch/feature'
import { useMoney } from '../../component/Money/useMoney'

interface SummaryImprovementsExperimentHook {
  enabled: boolean
  value: ExperimentValue
}

export const useSummaryImprovementsEligibility = (): boolean => {
  const country = useCountry()
  const isConsumerLending = useIsConsumerLendingOrder()
  const router = useRouter()
  const cohort = useCohort()
  const convergenceRollout = useRollout()

  // TODO: map this eligibility checker in a bird eye view
  const eligibleCohort = cohort !== Cohort.CashOnly
  const isOnSummaryPage = router.pathname === Route.SUMMARY

  const eligibleCountry = country !== Country.CA

  const conditions = [isOnSummaryPage, eligibleCountry, eligibleCohort, !isConsumerLending]
  const result = conditions.every(Boolean)

  return result
}

export const useSummaryImprovementsExperiment = (): SummaryImprovementsExperimentHook => {
  const isEligible = useSummaryImprovementsEligibility()
  // if not eligible, no need to fire the event
  const experiment = useExperiment('SummaryPageImprovements', isEligible)

  const { active, control, value } = experiment
  const enabled = isEligible && active && !control

  return { enabled, value }
}

export const useSchedulePreviewText = (): string => {
  const country = useCountry()

  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()
  const paymentSchedules = useCalculatedPaymentSchedules()
  const { locale } = useAppRouter()
  const installments = paymentSchedules.map((schedule) => schedule.amount)
  const { firstPaymentType } = useCheckoutState()
  const isNpuf = firstPaymentType === FirstPaymentType.NoPaymentUpfront
  const shouldUseZeroMoneyInstallment = isNpuf || isBillingAgreementApprovalProcess
  const zeroMoney = money('0.00', installments[0]?.currency)
  const [amountDueToday] = shouldUseZeroMoneyInstallment ? [zeroMoney] : installments
  const { formatMoney } = useMoney({
    decimalPlaces: 2,
    currency: amountDueToday.currency,
    locale,
  })

  const isPPDEnabled = useFlag(Features.ENABLE_ALIGN_PAYMENT_DAY)
  const { data: ppdData } = useGetPreferredPaymentDayQuery(undefined, { skip: country !== Country.US || !isPPDEnabled })
  const { t } = useTranslation()

  const hasActivePPD = isPPDEnabled && country === Country.US && ppdData?.isEligible && !!ppdData?.preferredPaymentDay
  const cohort = useCohort()
  const isAPOrNetNew = cohort === Cohort.APOnly || cohort === Cohort.NetNew

  if (isBillingAgreementApprovalProcess) {
    return t('summary:summaryPageImprovementExperiment:billingApprovalPreviewText', { cadence: 2, context: isAPOrNetNew ? 'estimated' : '' })
  }

  if (allAmountsSame(installments) && !isNpuf) {
    return t('summary:summaryPageImprovementExperiment:sameInstallment', {
      context: hasActivePPD ? 'ppd' : undefined,
      installment: formatMoney(amountDueToday.amount),
    })
  }

  const restOfInstallments = installments.slice(1)
  const restOfInstallmentsEqual = allAmountsSame(restOfInstallments)
  const idx = getHighestAmountIndex(restOfInstallments)
  const highestAmount = restOfInstallments[idx]

  if (allAmountsSame([amountDueToday, highestAmount])) {
    return t('summary:summaryPageImprovementExperiment:sameInstallment', {
      context: hasActivePPD ? 'ppdUpTo' : 'upTo',
      installment: formatMoney(highestAmount.amount),
    })
  }

  const pufContext = restOfInstallmentsEqual ? '' : 'upTo'
  const ppdPufContext = restOfInstallmentsEqual ? 'ppd' : 'ppdUpTo'

  const context = hasActivePPD ? ppdPufContext : pufContext

  return t('summary:summaryPageImprovementExperiment:puf', {
    context,
    installment: formatMoney(highestAmount.amount),
    amountDueToday: formatMoney(amountDueToday.amount),
  })
}

// Note: includes zero dollar payment schedule
export const useExperimentPaymentScheduleList = (): CheckoutPaymentSchedule[] => {
  const paymentSchedules = useCalculatedPaymentSchedules()
  const { firstPaymentType } = useCheckoutState()
  const isNpuf = firstPaymentType === FirstPaymentType.NoPaymentUpfront
  const zeroMoney = money('0.00', paymentSchedules[0]?.amount?.currency)
  const dateToday = new Date().toDateString()
  const zeroMoneyPaymentSchedule: CheckoutPaymentSchedule = { amount: zeroMoney, installmentDueDate: dateToday }

  return [...(isNpuf ? [zeroMoneyPaymentSchedule] : []), ...paymentSchedules]
}
